/**
 * Chart data alignment: map a norm (or other) series onto the index timeline by linear interpolation.
 *
 * Vegetation index: the **plot** only includes samples with finite raw value `>= INDEX_CHART_MASK_MIN`
 * (0.1); weaker values are omitted from labels and series (x-axis matches visible points). The same
 * sub-threshold days are still listed via {@link buildIndexLowSignalYmdSet} for chart masking.
 */

import {dateToYmd} from './field-details-date.util';

/** NDVI-like: plot and axis only for raw values >= this threshold; below → excluded from chart. */
export const INDEX_CHART_MASK_MIN = 0.1;

/**
 * Pairs of label + value for Chart.js: only indices where `rawValues[i]` is finite and `>= minPlot`.
 * Omits null/NaN and all values strictly below `minPlot` from both `chartLabels` and returned values.
 */
export function filterIndexChartSeriesForPlot(
  chartLabels: string[],
  rawValues: (number | null | undefined)[] | undefined,
  minPlot = INDEX_CHART_MASK_MIN,
): { chartLabels: string[]; values: number[] } {
  const labels: string[] = [];
  const values: number[] = [];
  const L = chartLabels || [];
  const V = rawValues || [];
  const len = Math.min(L.length, V.length);
  for (let i = 0; i < len; i++) {
    const v = V[i];
    if (v == null || !Number.isFinite(Number(v))) {
      continue;
    }
    const n = Number(v);
    if (n < minPlot) {
      continue;
    }
    labels.push(L[i]);
    values.push(n);
  }
  return { chartLabels: labels, values };
}

/**
 * Days (`YYYY-MM-DD`) where the index series has a finite value **strictly below** `minExclusive`
 * (includes negatives and `[0, minExclusive)`).
 * Intersect with `timelineDates` in the UI if needed for cross-filtering.
 */
export function buildIndexLowSignalYmdSet(
  chartLabels: string[],
  rawValues: (number | null | undefined)[] | undefined,
  minExclusive = INDEX_CHART_MASK_MIN,
): Set<string> {
  const out = new Set<string>();
  const labels = chartLabels || [];
  const vals = rawValues || [];
  const n = Math.min(labels.length, vals.length);
  for (let i = 0; i < n; i++) {
    const v = vals[i];
    if (v == null || !Number.isFinite(Number(v))) {
      continue;
    }
    const num = Number(v);
    if (num < minExclusive) {
      const d = new Date(labels[i]);
      if (!isNaN(d.getTime())) {
        out.add(dateToYmd(d));
      }
    }
  }
  return out;
}

/** Interpolates numeric samples defined on `sourceLabels` onto each `targetLabels` timestamp. */
export function interpolateSeriesToLabels(
  targetLabels: string[],
  sourceLabels: string[],
  sourceValues: (number | null)[],
): (number | null)[] {
  const points: { t: number; v: number }[] = [];
  for (let i = 0; i < sourceLabels.length; i++) {
    const t = new Date(sourceLabels[i]).getTime();
    const v = sourceValues[i];
    if (Number.isFinite(t) && v != null && Number.isFinite(Number(v))) {
      points.push({ t, v: Number(v) });
    }
  }
  points.sort((a, b) => a.t - b.t);
  if (!points.length) {
    return targetLabels.map(() => null);
  }
  if (points.length === 1) {
    return targetLabels.map(() => points[0].v);
  }
  return targetLabels.map(lbl => {
    const t = new Date(lbl).getTime();
    if (!Number.isFinite(t)) {
      return null;
    }
    if (t <= points[0].t) {
      return points[0].v;
    }
    if (t >= points[points.length - 1].t) {
      return points[points.length - 1].v;
    }
    for (let i = 0; i < points.length - 1; i++) {
      const left = points[i];
      const right = points[i + 1];
      if (t >= left.t && t <= right.t) {
        const span = right.t - left.t;
        if (span <= 0) {
          return left.v;
        }
        const ratio = (t - left.t) / span;
        return left.v + (right.v - left.v) * ratio;
      }
    }
    return null;
  });
}

/** Default Chart.js options for the field-details index line chart (theme-aware when `document` exists). */
export function buildFieldDetailsLineChartOptions(): any {
  let text = '#475569';
  let grid = '#e2e8f0';
  if (typeof document !== 'undefined') {
    const cs = getComputedStyle(document.documentElement);
    const tc = cs.getPropertyValue('--text-color').trim();
    const sb = cs.getPropertyValue('--surface-border').trim();
    if (tc) {
      text = tc;
    }
    if (sb) {
      grid = sb;
    }
  }
  return {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {legend: {labels: {color: text}}},
    scales: {
      x: {ticks: {color: text}, grid: {color: grid}},
      y: {ticks: {color: text}, grid: {color: grid}, suggestedMin: 0, suggestedMax: 1},
    },
  };
}
